/**
 * Ant Design `Form` replacement — a faithful, store-backed reimplementation of the
 * subset the app uses, so we can drop `import { Form } from 'antd'` WITHOUT changing
 * any validation rules or submission behaviour (the constraint that matters most).
 *
 * Supports: Form.useForm(), Form (form/initialValues/onFinish/onFinishFailed/
 * onValuesChange/layout/disabled), Form.Item (name/label/rules/required/valuePropName/
 * trigger/getValueFromEvent/dependencies/extra/help/validateStatus/noStyle/initialValue),
 * and the instance methods getFieldValue/getFieldsValue/setFieldsValue/setFieldValue/
 * resetFields/validateFields/submit/getFieldError. Rules: required/type(email,url,number)/
 * pattern/min/max/len/whitespace/validator.
 */
import {
  createContext, useContext, useEffect, useId, useRef, useState, cloneElement, isValidElement,
  type ReactNode, type ReactElement,
} from 'react';
import { cn } from './cn';

type NamePath = string | number | (string | number)[];
export interface Rule {
  required?: boolean;
  message?: ReactNode;
  type?: 'string' | 'number' | 'email' | 'url' | 'array' | 'integer';
  pattern?: RegExp;
  min?: number;
  max?: number;
  len?: number;
  whitespace?: boolean;
  /** A soft rule — its failure is shown but does NOT block submission (antd parity). */
  warningOnly?: boolean;
  validator?: (rule: Rule, value: any) => Promise<void> | void;
  transform?: (v: any) => any;
}
/** A rule, or an antd-style function rule that receives the form instance. */
export type RuleOrFn = Rule | ((form: FormInstance) => Rule);

const toPath = (n: NamePath): (string | number)[] => (Array.isArray(n) ? n : [n]);
const keyOf = (n: NamePath): string => toPath(n).join('.');
function getIn(obj: any, path: (string | number)[]) { return path.reduce((o, k) => (o == null ? undefined : o[k]), obj); }
function setIn(obj: any, path: (string | number)[], val: any) {
  const next = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur: any = next;
  for (let i = 0; i < path.length - 1; i++) {
    const k = path[i];
    cur[k] = cur[k] == null ? (typeof path[i + 1] === 'number' ? [] : {}) : Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[path[path.length - 1]] = val;
  return next;
}

interface Store {
  values: Record<string, any>;
  errors: Record<string, ReactNode[]>;
  initial: Record<string, any>;
  fields: Map<string, { name: NamePath }>;
  onValuesChange?: (changed: any, all: any) => void;
  onFinish?: (values: any) => void;
  onFinishFailed?: (info: any) => void;
  subs: Set<() => void>;
  emit(): void;
}

export interface FormInstance {
  getFieldValue(name: NamePath): any;
  getFieldsValue(nameList?: NamePath[] | true): any;
  setFieldsValue(values: Record<string, any>): void;
  setFieldValue(name: NamePath, value: any): void;
  resetFields(names?: NamePath[]): void;
  validateFields(nameList?: NamePath[]): Promise<any>;
  submit(): void;
  getFieldError(name: NamePath): ReactNode[];
  isFieldsTouched(): boolean;
  scrollToField(): void;
  /** @internal */ __store: Store;
  /** @internal */ __validators: Map<string, () => Promise<ReactNode[]>>;
}

function createStore(): Store {
  const store: Store = {
    values: {}, errors: {}, initial: {}, fields: new Map(), subs: new Set(),
    emit() { this.subs.forEach((f) => f()); },
  };
  return store;
}

function createInstance(): FormInstance {
  const store = createStore();
  const validators = new Map<string, () => Promise<ReactNode[]>>();
  const inst: FormInstance = {
    __store: store,
    __validators: validators,
    getFieldValue: (name) => getIn(store.values, toPath(name)),
    getFieldsValue: () => store.values,
    setFieldValue: (name, value) => { store.values = setIn(store.values, toPath(name), value); store.emit(); },
    setFieldsValue: (values) => { store.values = { ...store.values, ...values }; store.emit(); },
    resetFields: (names) => {
      if (names) names.forEach((n) => { store.values = setIn(store.values, toPath(n), getIn(store.initial, toPath(n))); delete store.errors[keyOf(n)]; });
      else { store.values = { ...store.initial }; store.errors = {}; }
      store.emit();
    },
    getFieldError: (name) => store.errors[keyOf(name)] ?? [],
    isFieldsTouched: () => true,
    scrollToField: () => {},
    validateFields: async () => {
      const results = await Promise.all([...validators.entries()].map(async ([k, run]) => [k, await run()] as const));
      const errs: Record<string, ReactNode[]> = {};
      for (const [k, e] of results) if (e.length) errs[k] = e;
      store.errors = errs;
      store.emit();
      if (Object.keys(errs).length) {
        const err = new Error('Validation failed');
        (err as any).errorFields = Object.entries(errs).map(([name, errors]) => ({ name: name.split('.'), errors }));
        (err as any).values = store.values;
        throw err;
      }
      return store.values;
    },
    submit: async () => {
      try {
        const values = await inst.validateFields();
        store.onFinish?.(values);
      } catch (e: any) {
        store.onFinishFailed?.({ values: store.values, errorFields: e?.errorFields ?? [] });
      }
    },
  };
  return inst;
}

/** antd `Form.useForm<Values>()` — returns a stable [instance]. The generic is accepted
 *  for antd source-compat; the instance API is untyped (any) internally. */
export function useForm<_Values = any>(external?: FormInstance): [FormInstance] {
  const ref = useRef<FormInstance>();
  if (!ref.current) ref.current = external ?? createInstance();
  return [ref.current];
}

// ── validation of a single rule ──────────────────────────────────────────────
async function runRule(rule: Rule, value: any, label: ReactNode): Promise<ReactNode | null> {
  const v = rule.transform ? rule.transform(value) : value;
  const empty = v == null || v === '' || (Array.isArray(v) && v.length === 0);
  const msg = rule.message ?? `${typeof label === 'string' ? label : 'This field'} is required`;
  if (rule.required && (empty || (rule.whitespace && typeof v === 'string' && v.trim() === ''))) return msg;
  if (empty) return null; // other rules don't apply to empty (non-required) values
  if (rule.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v))) return rule.message ?? 'Enter a valid email';
  if (rule.type === 'url' && !/^https?:\/\/.+/.test(String(v))) return rule.message ?? 'Enter a valid URL';
  if ((rule.type === 'number' || rule.type === 'integer') && Number.isNaN(Number(v))) return rule.message ?? 'Enter a valid number';
  if (rule.pattern && !rule.pattern.test(String(v))) return rule.message ?? 'Invalid format';
  if (typeof v === 'string' || Array.isArray(v)) {
    if (rule.len != null && v.length !== rule.len) return rule.message ?? `Must be ${rule.len} characters`;
    if (rule.min != null && v.length < rule.min) return rule.message ?? `Must be at least ${rule.min} characters`;
    if (rule.max != null && v.length > rule.max) return rule.message ?? `Must be at most ${rule.max} characters`;
  } else if (typeof v === 'number') {
    if (rule.min != null && v < rule.min) return rule.message ?? `Must be ≥ ${rule.min}`;
    if (rule.max != null && v > rule.max) return rule.message ?? `Must be ≤ ${rule.max}`;
  }
  if (rule.validator) {
    try { await rule.validator(rule, v); } catch (e: any) { return e?.message ?? rule.message ?? 'Invalid value'; }
  }
  return null;
}

// ── context ──────────────────────────────────────────────────────────────────
interface FormCtx { instance: FormInstance; layout: 'horizontal' | 'vertical' | 'inline'; disabled?: boolean; }
const Ctx = createContext<FormCtx | null>(null);
export const useFormContext = () => useContext(Ctx);

/** Name prefix injected by an enclosing Form.List — nested Form.Item names resolve
 *  relative to it (e.g. inside `<Form.List name="slabs">`, `name={[0,'qty']}` → `slabs.0.qty`). */
const ListPrefixCtx = createContext<(string | number)[]>([]);

export interface FormProps {
  form?: FormInstance;
  layout?: 'horizontal' | 'vertical' | 'inline';
  initialValues?: Record<string, any>;
  onFinish?: (values: any) => void;
  onFinishFailed?: (info: any) => void;
  onValuesChange?: (changed: any, all: any) => void;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: ReactNode;
  component?: false;
  requiredMark?: boolean | 'optional';
  id?: string;
  name?: string;
}

function FormRoot({ form, layout = 'vertical', initialValues, onFinish, onFinishFailed, onValuesChange, disabled, className, style, children }: FormProps) {
  const [internal] = useForm(form);
  const [, force] = useState(0);
  const store = internal.__store;

  // one-time init of initial values
  const inited = useRef(false);
  if (!inited.current) {
    inited.current = true;
    store.initial = { ...(initialValues ?? {}) };
    store.values = { ...(initialValues ?? {}) };
  }
  store.onValuesChange = onValuesChange;
  store.onFinish = onFinish;
  store.onFinishFailed = onFinishFailed;

  useEffect(() => {
    const rerender = () => force((n) => n + 1);
    store.subs.add(rerender);
    return () => { store.subs.delete(rerender); };
  }, [store]);

  const layoutCls = layout === 'inline' ? 'flex flex-wrap items-end gap-3' : 'space-y-4';
  return (
    <Ctx.Provider value={{ instance: internal, layout, disabled }}>
      <form
        className={cn(layoutCls, className)}
        style={style}
        noValidate
        onSubmit={(e) => { e.preventDefault(); internal.submit(); }}
      >
        {children}
      </form>
    </Ctx.Provider>
  );
}

export interface FormItemProps {
  name?: NamePath;
  label?: ReactNode;
  /** Permissive (`any[]`) to accept rule arrays from external validators whose `Rule`
   *  type differs structurally (e.g. antd's RuleRender function form); resolved +
   *  evaluated at runtime by runRule. */
  rules?: readonly any[];
  required?: boolean;
  valuePropName?: string;
  trigger?: string;
  getValueFromEvent?: (...args: any[]) => any;
  /** Transform the stored value into props spread onto the control (antd parity). */
  getValueProps?: (value: any) => Record<string, any>;
  /** Transform the control's emitted value before it is stored (antd parity). */
  normalize?: (value: any, prevValue: any, allValues: any) => any;
  initialValue?: any;
  dependencies?: NamePath[];
  extra?: ReactNode;
  help?: ReactNode;
  validateStatus?: 'success' | 'warning' | 'error' | 'validating';
  noStyle?: boolean;
  className?: string;
  style?: import('react').CSSProperties;
  labelCol?: unknown;
  wrapperCol?: unknown;
  hidden?: boolean;
  children?: ReactNode;
  tooltip?: ReactNode;
}

function FormItem(props: FormItemProps) {
  const {
    name, label, rules, required, valuePropName = 'value', trigger = 'onChange', getValueFromEvent, getValueProps, normalize,
    initialValue, extra, help, validateStatus, noStyle, className, style, hidden, children,
  } = props;
  const ctx = useContext(Ctx);
  const prefix = useContext(ListPrefixCtx);
  /*
   * One stable id per field, so the <label> can actually point at its control.
   *
   * The label was rendered unassociated: no `htmlFor`, no `id` on the child. A screen reader
   * therefore announced most fields with no name at all, clicking a label did not focus its input,
   * and nothing could be found by its accessible name — which is how this surfaced, when a driver
   * looking up "Tally invoice number" matched zero elements on a form that plainly shows the words.
   * Fixed at the kit layer because Form.Item is the field wrapper for every form in the app.
   */
  const fieldId = useId();
  if (!ctx || name == null) {
    /*
     * No form context or nameless item — render children as-is (decorative wrapper).
     *
     * The `required` MARK is drawn here too. This branch used to print the bare label, so every
     * `<Form.Item label="…" required>` that holds a control the form does not own — a file upload,
     * a custom picker, anything driven by its own useState — declared itself required and showed
     * nothing for it. Twelve fields across the app said "required" in the source and looked optional
     * on screen, including the Tally invoice PDF, which the server has always rejected without.
     * A field that is mandatory has to LOOK mandatory before the reader presses Save.
     */
    if (noStyle) return <>{children}</>;
    return (
      <div style={style} className={cn(ctx?.layout !== 'inline' && 'mb-4', hidden && 'hidden', className)}>
        {label != null && (
          <label htmlFor={fieldId} className="mb-1.5 block type-body-medium text-content">
            {label}
            {required && <span className="ml-0.5 text-danger-500">*</span>}
          </label>
        )}
        {isValidElement(children) && (children as ReactElement).props.id == null
          ? cloneElement(children as ReactElement, { id: fieldId })
          : children}
        {/* `help` + `validateStatus` are honoured here as well. The form owns no value for this
            field, so a caller that validates it itself (a file upload, a custom picker) has nowhere
            else to put the complaint — without this it passed `help` and got silence. */}
        {help != null && (
          <div className={cn('mt-1 text-xs', validateStatus === 'error' ? 'text-danger-500' : 'text-subtle')}
            role={validateStatus === 'error' ? 'alert' : undefined}>
            {help}
          </div>
        )}
        {extra != null && <div className="mt-1 text-xs text-subtle">{extra}</div>}
      </div>
    );
  }
  const { instance, disabled } = ctx;
  const store = instance.__store;
  // Full path = enclosing Form.List prefix + this item's name (empty prefix at top level).
  const path = [...prefix, ...toPath(name)];
  const k = path.join('.');

  // seed initialValue once
  const seeded = useRef(false);
  if (!seeded.current) {
    seeded.current = true;
    if (initialValue !== undefined && getIn(store.values, path) === undefined) {
      store.values = setIn(store.values, path, initialValue);
      store.initial = setIn(store.initial, path, initialValue);
    }
  }

  // register validator for this field once (reads the latest rules via a ref so
  // inline rule arrays / function rules don't churn the registration)
  const rulesRef = useRef(rules);
  rulesRef.current = rules;
  const labelRef = useRef(label);
  labelRef.current = label;
  useEffect(() => {
    instance.__validators.set(k, async () => {
      const value = getIn(store.values, path);
      const rs = rulesRef.current ?? [];
      const errs: ReactNode[] = [];
      for (const raw of rs) {
        const r = (typeof raw === 'function' ? raw(instance) : raw) as Rule; // resolve antd-style function rules
        const e = await runRule(r, value, labelRef.current);
        if (e && !r.warningOnly) { errs.push(e); break; } // warningOnly rules never block submit
      }
      return errs;
    });
    return () => { instance.__validators.delete(k); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [k, store, instance]);

  const value = getIn(store.values, path);
  const errors = store.errors[k] ?? [];
  const hasError = errors.length > 0 || validateStatus === 'error';
  const isRequired = required ?? rules?.some((r: any) => typeof r !== 'function' && r.required);

  const handle = (...args: any[]) => {
    let next: any;
    if (getValueFromEvent) next = getValueFromEvent(...args);
    else {
      const a = args[0];
      next = a && typeof a === 'object' && 'target' in a ? (valuePropName === 'checked' ? (a.target as HTMLInputElement).checked : (a.target as HTMLInputElement).value) : a;
    }
    const prev = store.values;
    if (normalize) next = normalize(next, getIn(store.values, path), store.values);
    store.values = setIn(store.values, path, next);
    if (hasError) { const e = { ...store.errors }; delete e[k]; store.errors = e; }
    store.onValuesChange?.(setIn({}, path, next), store.values);
    store.emit();
    void prev;
  };

  let control: ReactNode = children;
  if (isValidElement(children)) {
    /*
     * CHAIN the field's own handler — never replace it.
     *
     * antd calls the child's onChange as well as the form's. This clone used to pass only `handle`,
     * so every `<Form.Item name="x"><Select onChange={…}/></Form.Item>` lost its side effect: in the
     * Record Payment modal, picking a retailer never ran `setRetailer`, so "Against order" stayed
     * disabled forever and no payment could be linked to an order from that form. The bug is invisible
     * — the field still stores its value, only the caller's reaction is dropped.
     */
    const own = (children as ReactElement).props as Record<string, any>;
    const chain = (mine: any, theirs: any) => (theirs ? (...args: any[]) => { theirs(...args); mine(...args); } : mine);
    const childProps: Record<string, any> = getValueProps
      ? { ...getValueProps(value), [trigger]: chain(handle, own?.[trigger]) }
      : { [valuePropName]: value ?? (valuePropName === 'checked' ? false : ''), [trigger]: chain(handle, own?.[trigger]) };
    if (disabled) childProps.disabled = true;
    // Label association + validation state, unless the caller already set them. `id` is what makes
    // the <label htmlFor> above point somewhere; `aria-invalid` and `aria-describedby` connect the
    // control to the error line that renders under it.
    if (own?.id == null) childProps.id = fieldId;
    if (own?.['aria-invalid'] == null && hasError) childProps['aria-invalid'] = true;
    if (own?.['aria-describedby'] == null && (help != null || hasError)) childProps['aria-describedby'] = `${fieldId}-help`;
    // Also pass `onCheckedChange` for ui Switch/Checkbox that use it (when using the default trigger).
    if (valuePropName === 'checked' && trigger === 'onChange') {
      childProps.onCheckedChange = chain((c: boolean) => handle(c), own?.onCheckedChange);
    }
    control = cloneElement(children as ReactElement, childProps);
  }

  if (noStyle) return <>{hasError && help == null ? control : control}</>;

  return (
    // A consistent bottom margin so every field self-spaces — vital inside <Row><Col> grids where the
    // form's `space-y-4` doesn't reach the items, which otherwise left each label as close to the field
    // ABOVE it as to its own (labels then read as belonging to the wrong field). Skipped for inline forms.
    <div className={cn(ctx?.layout !== 'inline' && 'mb-4', hidden && 'hidden', className)}>
      {label != null && (
        <label htmlFor={fieldId} className="mb-1.5 block type-body-medium text-content">
          {label}
          {isRequired && <span className="ml-0.5 text-danger-500">*</span>}
        </label>
      )}
      {control}
      {(help != null || hasError) && (
        <div id={`${fieldId}-help`} className={cn('mt-1 text-xs', hasError ? 'text-danger-500' : 'text-subtle')} role={hasError ? 'alert' : undefined}>
          {help ?? errors[0]}
        </div>
      )}
      {extra != null && <div className="mt-1 text-xs text-subtle">{extra}</div>}
    </div>
  );
}

/** antd `Form.useWatch(name, form)` — subscribe to one field's value. */
export function useWatch<_T = any>(name: NamePath, form?: FormInstance): any {
  const ctx = useContext(Ctx);
  const inst = form ?? ctx?.instance;
  const [, force] = useState(0);
  useEffect(() => {
    if (!inst) return;
    const r = () => force((n) => n + 1);
    inst.__store.subs.add(r);
    return () => { inst.__store.subs.delete(r); };
  }, [inst]);
  return inst ? getIn(inst.__store.values, toPath(name)) : undefined;
}

// ── Form.List (dynamic field arrays) ─────────────────────────────────────────
interface ListField { key: number; name: number; }
interface ListOperations {
  add: (defaultValue?: any, insertIndex?: number) => void;
  remove: (index: number | number[]) => void;
  move: (from: number, to: number) => void;
}
export interface FormListProps {
  name: NamePath;
  initialValue?: any[];
  children: (fields: (ListField & { fieldKey: number })[], operations: ListOperations, meta: { errors: ReactNode[] }) => ReactNode;
}

/**
 * antd `Form.List` — renders a dynamic array of fields. Nested Form.Items author
 * their `name` relative to each row (`[field.name, 'sub']`); a prefix context
 * resolves those to the absolute store path. Uses stable per-row keys (antd parity)
 * so removing/reordering rows doesn't scramble React state.
 */
function FormList({ name, initialValue, children }: FormListProps) {
  const ctx = useContext(Ctx);
  const prefix = useContext(ListPrefixCtx);
  const [, force] = useState(0);
  const store = ctx?.instance.__store;

  useEffect(() => {
    if (!store) return;
    const r = () => force((n) => n + 1);
    store.subs.add(r);
    return () => { store.subs.delete(r); };
  }, [store]);

  const path = ctx ? [...prefix, ...toPath(name)] : [];

  // Seed initialValue once.
  const seeded = useRef(false);
  if (store && !seeded.current) {
    seeded.current = true;
    if (initialValue !== undefined && getIn(store.values, path) === undefined) {
      store.values = setIn(store.values, path, initialValue);
      store.initial = setIn(store.initial, path, initialValue);
    }
  }

  const arr: any[] = (store ? getIn(store.values, path) : undefined) ?? [];

  // Stable keys, one per row, kept in lock-step with the data array.
  const counter = useRef(0);
  const keys = useRef<number[]>([]);
  if (keys.current.length !== arr.length) {
    // Reconcile length (covers external setFieldsValue / initial seeding).
    keys.current = arr.map((_, i) => keys.current[i] ?? counter.current++);
  }

  const setArr = (next: any[], nextKeys: number[]) => {
    keys.current = nextKeys;
    if (store) { store.values = setIn(store.values, path, next); store.emit(); }
  };
  const ops: ListOperations = {
    add: (defaultValue, insertIndex) => {
      const next = [...arr];
      const nk = [...keys.current];
      const at = insertIndex == null || insertIndex > next.length ? next.length : insertIndex;
      next.splice(at, 0, defaultValue);
      nk.splice(at, 0, counter.current++);
      setArr(next, nk);
    },
    remove: (index) => {
      const idxs = new Set(Array.isArray(index) ? index : [index]);
      setArr(arr.filter((_, i) => !idxs.has(i)), keys.current.filter((_, i) => !idxs.has(i)));
    },
    move: (from, to) => {
      if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return;
      const next = [...arr]; const nk = [...keys.current];
      const [it] = next.splice(from, 1); const [kk] = nk.splice(from, 1);
      next.splice(to, 0, it); nk.splice(to, 0, kk);
      setArr(next, nk);
    },
  };

  const fields = arr.map((_, i) => ({ key: keys.current[i], name: i, fieldKey: keys.current[i] }));
  return <ListPrefixCtx.Provider value={path}>{children(fields, ops, { errors: [] })}</ListPrefixCtx.Provider>;
}

export const Form = Object.assign(FormRoot, { Item: FormItem, List: FormList, useForm, useWatch, useFormInstance: () => useContext(Ctx)?.instance });
